import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import useLoginStore from "@/stores/loginStore";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import { PaginationComponent } from "@/components/PaginationComponent";
import {
  useQueryGetUserList,
  type IUserListParams,
} from "@/apis/users/getUserList";
import { getPaginationInfo } from "@/types/pagination";
import UserListTable from "./UserListTable";
import ActiveFilters, { type Filter } from "@/components/ActiveFilters";
import FilteringUserListModal, {
  type FilterState,
} from "./components/FilteringUserListModal";
import { useQueryGetGroupList } from "@/apis/groups/getGroupList";

const USER_LIST_SETTINGS_KEY = "userListSettings";

type SortField = "grade" | "name" | "school" | null;
type SortOrder = "asc" | "desc";

interface UserListSettings {
  filters: FilterState;
  sortField: SortField;
  sortOrder: SortOrder;
}

const defaultSettings: UserListSettings = {
  filters: { gradeFilter: "all", groupFilter: "all" },
  sortField: null,
  sortOrder: "desc",
};

const UserListPage: React.FC = () => {
  const { clearToken } = useLoginStore();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Load initial settings from localStorage once
  const initialSettings = useState(() => {
    try {
      const savedSettings = localStorage.getItem(USER_LIST_SETTINGS_KEY);
      if (savedSettings) {
        const parsedSettings: UserListSettings = JSON.parse(savedSettings);
        // Ensure groupFilter exists in saved settings for backward compatibility
        if (!parsedSettings.filters.groupFilter) {
          parsedSettings.filters.groupFilter = "all";
        }
        return parsedSettings;
      }
    } catch (error) {
      console.error("Failed to load settings from localStorage:", error);
    }
    return defaultSettings;
  })[0];

  // Filter state with loaded initial values
  const [filters, setFilters] = useState<FilterState>(initialSettings.filters);

  // Sorting state with loaded initial values
  const [sortField, setSortField] = useState<SortField>(
    initialSettings.sortField
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    initialSettings.sortOrder
  );

  const [searchParams, setSearchParams] = useState<IUserListParams>({
    page: 1,
    limit: 10,
    role: "student",
  });

  // Get group list for filter display
  const { data: groupsData } = useQueryGetGroupList({ limit: 100 });

  // Save settings to localStorage when they change
  useEffect(() => {
    try {
      const settings: UserListSettings = {
        filters,
        sortField,
        sortOrder,
      };
      localStorage.setItem(USER_LIST_SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error("Failed to save settings to localStorage:", error);
    }
  }, [filters, sortField, sortOrder]);

  // Update search params when filters or sorting change
  useEffect(() => {
    setSearchParams((prev) => ({
      ...prev,
      grade:
        filters.gradeFilter === "all"
          ? undefined
          : parseInt(filters.gradeFilter),
      group_id: filters.groupFilter === "all" ? undefined : filters.groupFilter,
      sort: sortField || undefined,
      sort_order: sortField ? sortOrder : undefined,
      page: 1, // Reset to first page when filters or sorting change
    }));
  }, [filters, sortField, sortOrder]);

  // API 호출
  const { data, isLoading, error } = useQueryGetUserList(searchParams);

  // 검색 처리
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => ({
      ...prev,
      search: searchTerm,
      page: 1, // 검색 시 첫 페이지로 이동
    }));
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        // asc → desc
        setSortOrder("desc");
      } else {
        // desc → 정렬 해제
        setSortField(null);
      }
    } else {
      // New field clicked, set to ascending
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Filter modal handlers
  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleRemoveFilter = (filterKey: string) => {
    if (filterKey === "gradeFilter") {
      setFilters((prev) => ({
        ...prev,
        gradeFilter: "all",
      }));
    } else if (filterKey === "groupFilter") {
      setFilters((prev) => ({
        ...prev,
        groupFilter: "all",
      }));
    }
  };

  const handleClearAllFilters = () => {
    setFilters({
      gradeFilter: "all",
      groupFilter: "all",
    });
  };

  // Get group name by ID
  const getGroupName = (groupId: string) => {
    const group = groupsData?.items?.find((g) => g.id === groupId);
    return group?.name || "Unknown Group";
  };

  // Get active filters for display
  const getActiveFilters = (): Filter[] => {
    const activeFilters: Filter[] = [];

    if (filters.gradeFilter !== "all") {
      activeFilters.push({
        key: "gradeFilter",
        label: `Grade ${filters.gradeFilter}`,
        value: filters.gradeFilter,
      });
    }

    if (filters.groupFilter !== "all") {
      activeFilters.push({
        key: "groupFilter",
        label: getGroupName(filters.groupFilter),
        value: filters.groupFilter,
      });
    }

    return activeFilters;
  };

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  // 학생 추가 페이지로 이동
  const handleAddStudent = () => {
    navigate("/users/create");
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Student List</Heading>
        <Text className="text-zinc-500">
          View all students registered at the academy. Use the search to find
          specific students.
        </Text>

        <div className="flex justify-between mt-4">
          <div className="flex items-center gap-3">
            <Button onClick={handleAddStudent}>
              <PlusIcon />
              Add Student
            </Button>

            <Button onClick={handleOpenFilterModal}>
              <FunnelIcon />
              Filter
            </Button>
          </div>

          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                name="search"
                placeholder="name, email, phone"
                aria-label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {/* Active Filters */}
        <ActiveFilters
          filters={getActiveFilters()}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />

        {/* User List Table Component */}
        <UserListTable
          users={data?.items || []}
          isLoading={isLoading}
          error={error as Error | null}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
        />

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Filter Modal */}
        <FilteringUserListModal
          open={isFilterModalOpen}
          onClose={handleCloseFilterModal}
          onApply={handleApplyFilters}
          currentFilters={filters}
        />
      </div>
    </PageContainer>
  );
};

export default UserListPage;
